import { getTenantConfig } from '../../config/tenantConfig';
import httpClient from './httpClient';

/**
 * Dataset Management API Service
 * Handles all dataset operations (CRUD, import, status)
 * Uses httpClient for automatic 401 handling and token refresh
 */

/**
 * Get all datasets for current tenant
 * @param {Object} options - Query options
 * @param {boolean} options.includeInactive - Include inactive datasets (default: true for admin UI)
 * @returns {Promise<Array>} Array of dataset objects
 */
export async function listDatasets(options = {}) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      console.error('Dataset Management API endpoint not configured');
      throw new Error('Dataset Management API not configured. Run bin/frontend/configure-api-endpoints.sh');
    }

    // Build query string - default to including inactive for admin UI
    const includeInactive = options.includeInactive !== false;
    const params = includeInactive ? { include_inactive: true } : {};

    const result = await httpClient.get(`${datasetApi}/datasets`, { params });
    return result.datasets || [];
  } catch (error) {
    console.error('Error fetching datasets:', error);
    throw error;
  }
}

/**
 * Get a specific dataset by ID
 * @param {string} datasetId - Dataset ID
 * @returns {Promise<Object>} Dataset object
 */
export async function getDataset(datasetId) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    return await httpClient.get(`${datasetApi}/datasets/${datasetId}`);
  } catch (error) {
    console.error(`Error fetching dataset ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Create a new dataset
 * @param {Object} datasetData - Dataset creation data
 * @param {string} datasetData.dataset_id - Unique dataset ID (uppercase letters/numbers only)
 * @param {string} datasetData.dataset_name - Human-readable dataset name
 * @param {string} datasetData.description - Dataset description
 * @returns {Promise<Object>} Created dataset object
 */
export async function createDataset(datasetData) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    return await httpClient.post(`${datasetApi}/datasets`, datasetData);
  } catch (error) {
    console.error('Error creating dataset:', error);
    throw error;
  }
}

/**
 * Update dataset metadata
 * @param {string} datasetId - Dataset ID
 * @param {Object} updates - Fields to update (dataset_name, description, status)
 * @returns {Promise<Object>} Updated dataset object
 */
export async function updateDataset(datasetId, updates) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    return await httpClient.put(`${datasetApi}/datasets/${datasetId}`, updates);
  } catch (error) {
    console.error(`Error updating dataset ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Update dataset status (ACTIVE/INACTIVE)
 * @param {string} datasetId - Dataset ID
 * @param {string} status - New status (ACTIVE or INACTIVE)
 * @returns {Promise<Object>} Updated dataset
 */
export async function updateDatasetStatus(datasetId, status) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    return await httpClient.put(`${datasetApi}/datasets/${datasetId}/status`, { status });
  } catch (error) {
    console.error(`Error updating dataset status ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Delete a dataset
 * @param {string} datasetId - Dataset ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export async function deleteDataset(datasetId) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    return await httpClient.delete(`${datasetApi}/datasets/${datasetId}`);
  } catch (error) {
    console.error(`Error deleting dataset ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Get pre-signed URL for uploading dataset import file
 * @param {string} datasetId - Dataset ID
 * @param {string} filename - Import filename
 * @returns {Promise<Object>} Object with uploadUrl and importKey
 */
export async function getUploadUrl(datasetId, filename) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    return await httpClient.post(`${datasetApi}/datasets/${datasetId}/import`, { filename });
  } catch (error) {
    console.error(`Error getting upload URL for dataset ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Upload dataset import file to S3
 * @param {string} uploadUrl - Pre-signed S3 upload URL
 * @param {File} file - File object to upload
 * @param {Function} onProgress - Progress callback (percentage)
 * @returns {Promise<void>}
 */
export async function uploadDatasetFile(uploadUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percentage = Math.round((e.loaded / e.total) * 100);
        onProgress(percentage);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    // Set Content-Type to match what was signed in the presigned URL
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.send(file);
  });
}

/**
 * Get import status for a dataset
 * @param {string} datasetId - Dataset ID
 * @returns {Promise<Object>} Import status object
 */
export async function getImportStatus(datasetId) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    return await httpClient.get(`${datasetApi}/datasets/${datasetId}/import-status`);
  } catch (error) {
    console.error(`Error getting import status for dataset ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Upload dataset icon to S3
 * @param {string} datasetId - Dataset ID
 * @param {File} iconFile - Icon image file
 * @returns {Promise<string>} S3 key of uploaded icon
 */
export async function uploadDatasetIcon(datasetId, iconFile) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    // Convert file to base64
    const fileData = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(iconFile);
    });

    // Upload via API (server-side S3 upload)
    const { s3Key, iconUrl } = await httpClient.post(`${datasetApi}/datasets/${datasetId}/icon`, {
      fileData,
      filename: iconFile.name,
      contentType: iconFile.type
    });

    return s3Key;
  } catch (error) {
    console.error(`Error uploading icon for dataset ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Upload dataset icon from URL (server-side download to bypass CORS)
 * @param {string} datasetId - Dataset ID
 * @param {string} iconUrl - URL to icon image
 * @returns {Promise<string>} S3 key of uploaded icon
 */
export async function uploadDatasetIconFromUrl(datasetId, iconUrl) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    // Send URL to backend (server downloads and uploads to S3)
    const { s3Key } = await httpClient.post(`${datasetApi}/datasets/${datasetId}/icon`, {
      iconUrl
    });

    return s3Key;
  } catch (error) {
    console.error(`Error uploading icon from URL for dataset ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Upload group icon from URL (server-side download to bypass CORS)
 * @param {string} datasetId - Dataset ID
 * @param {string} groupCode - Group code
 * @param {string} iconUrl - URL to icon image
 * @returns {Promise<{s3Key: string, iconUrl: string}>} S3 key and URL of uploaded icon
 */
export async function uploadGroupIconFromUrl(datasetId, groupCode, iconUrl) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      throw new Error('Dataset Management API not configured');
    }

    // Send URL to backend (server downloads and uploads to S3)
    const { s3Key, iconUrl: uploadedUrl } = await httpClient.post(
      `${datasetApi}/datasets/${datasetId}/groups/${groupCode}/icon`,
      { iconUrl }
    );

    return { s3Key, iconUrl: uploadedUrl };
  } catch (error) {
    console.error(`Error uploading icon from URL for group ${groupCode} in dataset ${datasetId}:`, error);
    throw error;
  }
}

/**
 * Get import log for a specific import
 * @param {string} datasetId - Dataset ID
 * @param {string} importId - Import ID
 * @returns {Promise<Object>} Import log with per-person details
 */
export async function getImportLog(datasetId, importId) {
  try {
    const config = getTenantConfig();
    const datasetApi = config.api.datasetManagement;

    if (!datasetApi) {
      console.error('Dataset Management API endpoint not configured');
      throw new Error('Dataset Management API not configured. Run bin/frontend/configure-api-endpoints.sh');
    }

    const result = await httpClient.get(`${datasetApi}/datasets/${datasetId}/imports/${importId}/log`);
    return result;
  } catch (error) {
    console.error(`Error fetching import log for ${datasetId}/${importId}:`, error);
    throw error;
  }
}
